import React, { useState, useEffect } from 'react';
import axios from '../../../config/axios';
import { Button, Meta, Display, Chip } from '../../components/Atoms';
import { LoadingState, ErrorState } from '../../components/states';
import { useApi } from '../../lib/api';
import ProfileShell, { ProfileLabel, ProfileSection, profileInputStyle } from './ProfileShell';

/**
 * ProfilePublic - kompletny edytor profilu publicznego.
 *
 * Endpointy:
 *   GET  /api/profile/public                - aktualne ustawienia + lista bedów
 *   POST /api/profile/public                - zapis pól
 *   GET  /api/profile/public/photos/available - zdjęcia do pickera
 *   POST /api/profile/public/photos         - zapis galerii
 *   GET  /api/profile/public/stats          - widoki profilu
 */

const GARDEN_TYPES = [
  { v: '',          l: '— nie określam —' },
  { v: 'vegetable', l: 'Warzywnik' },
  { v: 'flowers',   l: 'Rabata kwiatowa' },
  { v: 'balcony',   l: 'Balkon / parapet' },
  { v: 'orchard',   l: 'Sad / drzewa owocowe' },
  { v: 'herbs',     l: 'Zioła i przyprawy' },
  { v: 'mixed',     l: 'Mieszany / wszystko po trochu' },
];

const SOCIAL_PLACEHOLDERS = {
  socialInstagram: 'https://instagram.com/twoj_profil',
  socialFacebook:  'https://facebook.com/twoj_profil',
  socialYoutube:   'https://youtube.com/@twoj_kanal',
  socialPinterest: 'https://pinterest.com/twoj_profil',
  socialWebsite:   'https://twojstrona.pl',
};

const SOCIAL_LABELS = {
  socialInstagram: 'Instagram',
  socialFacebook:  'Facebook',
  socialYoutube:   'YouTube',
  socialPinterest: 'Pinterest',
  socialWebsite:   'Strona / blog',
};

// ── Picker zdjęć (cover + galeria) ──────────────────────────────────
function PhotoPickerModal({ open, onClose, mode, currentCoverId, currentGalleryIds, onSave }) {
  const { data: photos, loading } = useApi(open ? '/api/profile/public/photos/available' : null);
  const [selectedSingle, setSelectedSingle] = useState(currentCoverId || null);
  const [selectedMulti, setSelectedMulti] = useState([]);

  useEffect(() => {
    if (open) {
      setSelectedSingle(currentCoverId || null);
      setSelectedMulti(currentGalleryIds || []);
    }
  }, [open, currentCoverId, currentGalleryIds]);

  if (!open) return null;

  const isCover = mode === 'cover';

  const handleConfirm = () => {
    if (isCover) onSave(selectedSingle);
    else onSave(selectedMulti);
  };

  const isSelected = (id) => isCover ? selectedSingle === id : selectedMulti.includes(id);

  const toggle = (id) => {
    if (isCover) {
      setSelectedSingle(id === selectedSingle ? null : id);
    } else {
      setSelectedMulti(prev =>
        prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id].slice(0, 50)
      );
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100,
      background: 'rgba(15, 20, 16, 0.85)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 20,
    }}
    onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-line)',
          borderRadius: 16,
          maxWidth: 880, width: '100%',
          maxHeight: '85dvh', overflow: 'hidden',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <Display size={22} style={{ display: 'block' }}>
              {isCover ? 'Zdjęcie tła profilu' : 'Galeria publiczna'}
            </Display>
            <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginTop: 4 }}>
              {isCover
                ? 'Wybierz jedno zdjęcie — pojawi się jako tło w hero profilu.'
                : `Wybrane: ${selectedMulti.length} / 50 — przeciągnij i kliknij, żeby zaznaczyć.`}
            </div>
          </div>
          <button
            type="button" onClick={onClose}
            style={{
              background: 'transparent', border: 'none',
              color: 'var(--t-text-mute)', cursor: 'pointer',
              fontSize: 24, lineHeight: 1,
            }}
            aria-label="Zamknij"
          >×</button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 20 }}>
          {loading ? (
            <LoadingState />
          ) : !photos || photos.length === 0 ? (
            <div style={{
              padding: '40px 20px', textAlign: 'center',
              color: 'var(--t-text-mute)', fontSize: 14,
              fontStyle: 'italic',
            }}>
              ↳ Brak zdjęć w Twojej galerii. Dodaj zdjęcia z grządek (Galeria → +).
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
              gap: 10,
            }}>
              {photos.map(p => {
                const sel = isSelected(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggle(p.id)}
                    style={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      borderRadius: 10,
                      border: `2px solid ${sel ? 'var(--t-sage)' : 'var(--t-line)'}`,
                      background: 'var(--t-bg-elev)',
                      cursor: 'pointer', padding: 0,
                      overflow: 'hidden',
                      transition: 'all 200ms ease',
                    }}
                  >
                    <img
                      src={p.path?.startsWith('/') ? p.path : `/${p.path}`}
                      alt={p.caption || ''}
                      loading="lazy"
                      style={{
                        width: '100%', height: '100%',
                        objectFit: 'cover', display: 'block',
                      }}
                      onError={e => { e.currentTarget.style.opacity = 0.3; }}
                    />
                    {sel && (
                      <div style={{
                        position: 'absolute', top: 6, right: 6,
                        width: 24, height: 24, borderRadius: '50%',
                        background: 'var(--t-sage)', color: 'var(--t-bg)',
                        display: 'grid', placeItems: 'center',
                        fontSize: 12, fontWeight: 600,
                      }}>✓</div>
                    )}
                    {p.caption && (
                      <div style={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        padding: '6px 8px',
                        background: 'linear-gradient(transparent, rgba(15,20,16,.9))',
                        fontSize: 10, color: 'var(--t-text)',
                        fontFamily: 'var(--t-mono)', letterSpacing: '.04em',
                        textAlign: 'left', lineHeight: 1.3,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      }}>{p.caption}</div>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid var(--t-line)',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <Button variant="ghost" onClick={onClose}>Anuluj</Button>
          <Button variant="primary" onClick={handleConfirm}>
            {isCover ? (selectedSingle ? 'Wybierz zdjęcie' : 'Bez zdjęcia') : `Zapisz (${selectedMulti.length})`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Główny komponent ────────────────────────────────────────────────
export default function ProfilePublic() {
  const { data, loading, error, refetch } = useApi('/api/profile/public');
  const { data: stats } = useApi('/api/profile/public/stats');

  // Ładujemy zdjęcia tylko gdy potrzeba (do mapowania selected gallery → ids)
  const { data: availablePhotos } = useApi('/api/profile/public/photos/available');

  const [form, setForm] = useState({
    enabled: false,
    username: '',
    displayName: '',
    bio: '',
    showStats: true,
    showTimeline: true,
    showGallery: true,
    showBadges: true,
    socialInstagram: '',
    // Nowe pola
    tagline: '',
    aboutLong: '',
    gardenType: '',
    showLocation: false,
    showLocationPin: false,
    featuredBedId: '',
    socialFacebook: '',
    socialYoutube: '',
    socialPinterest: '',
    socialWebsite: '',
    coverPhotoId: null,
  });
  const [galleryIds, setGalleryIds] = useState([]);
  const [originalGalleryIds, setOriginalGalleryIds] = useState([]);
  const [pickerMode, setPickerMode] = useState(null); // 'cover' | 'gallery' | null
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (data) {
      setForm({
        enabled: !!data.enabled,
        username: data.username || '',
        displayName: data.displayName || '',
        bio: data.bio || '',
        showStats: !!data.showStats,
        showTimeline: !!data.showTimeline,
        showGallery: !!data.showGallery,
        showBadges: !!data.showBadges,
        socialInstagram: data.socialInstagram || '',
        tagline: data.tagline || '',
        aboutLong: data.aboutLong || '',
        gardenType: data.gardenType || '',
        showLocation: !!data.showLocation,
        showLocationPin: !!data.showLocationPin,
        featuredBedId: data.featuredBedId || '',
        socialFacebook: data.socialFacebook || '',
        socialYoutube: data.socialYoutube || '',
        socialPinterest: data.socialPinterest || '',
        socialWebsite: data.socialWebsite || '',
        coverPhotoId: data.coverPhotoId || null,
      });
    }
  }, [data]);

  // Synchronizuj galeryIds z faktycznych zaznaczeń w endpoint photos/available
  useEffect(() => {
    if (availablePhotos) {
      const ids = availablePhotos.filter(p => p.isSelected).map(p => p.id);
      setGalleryIds(ids);
      setOriginalGalleryIds(ids);
    }
  }, [availablePhotos]);

  if (error?.status === 401) {
    return <ProfileShell><ErrorState error={error} /></ProfileShell>;
  }
  if (loading) return <ProfileShell><LoadingState /></ProfileShell>;

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const publicUrl = form.username ? `gardenapp.pl/g/${form.username}` : null;
  const publicUrlFull = form.username ? `https://gardenapp.pl/g/${form.username}` : null;

  const beds = data?.beds || [];
  const featuredBedLabel = beds.find(b => String(b.id) === String(form.featuredBedId))?.label;
  const coverPhoto = availablePhotos?.find(p => p.id === form.coverPhotoId);

  const handleCopyUrl = () => {
    if (publicUrlFull && navigator.clipboard) {
      navigator.clipboard.writeText(publicUrlFull);
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 1500);
    }
  };

  const galleryDirty = JSON.stringify(galleryIds.slice().sort()) !== JSON.stringify(originalGalleryIds.slice().sort());

  const isDirty = data && (
    !!form.enabled !== !!data.enabled ||
    form.username !== (data.username || '') ||
    form.displayName !== (data.displayName || '') ||
    form.bio !== (data.bio || '') ||
    !!form.showStats !== !!data.showStats ||
    !!form.showTimeline !== !!data.showTimeline ||
    !!form.showGallery !== !!data.showGallery ||
    !!form.showBadges !== !!data.showBadges ||
    form.socialInstagram !== (data.socialInstagram || '') ||
    form.tagline !== (data.tagline || '') ||
    form.aboutLong !== (data.aboutLong || '') ||
    form.gardenType !== (data.gardenType || '') ||
    !!form.showLocation !== !!data.showLocation ||
    !!form.showLocationPin !== !!data.showLocationPin ||
    String(form.featuredBedId || '') !== String(data.featuredBedId || '') ||
    form.socialFacebook !== (data.socialFacebook || '') ||
    form.socialYoutube !== (data.socialYoutube || '') ||
    form.socialPinterest !== (data.socialPinterest || '') ||
    form.socialWebsite !== (data.socialWebsite || '') ||
    form.coverPhotoId !== data.coverPhotoId ||
    galleryDirty
  );

  const handleSave = async () => {
    setSaving(true);
    setSaveError('');
    try {
      // 1. Zapisz pola profilu
      await axios.post('/api/profile/public', {
        ...form,
        featuredBedId: form.featuredBedId === '' ? null : parseInt(form.featuredBedId, 10),
      });
      // 2. Jeśli zmieniła się galeria — zapisz osobno
      if (galleryDirty) {
        await axios.post('/api/profile/public/photos', { photoIds: galleryIds });
      }
      setSavedAt(new Date());
      refetch();
      setOriginalGalleryIds(galleryIds);
    } catch (err) {
      setSaveError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Nie udało się zapisać.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileShell
      eyebrow="Profil publiczny"
      title="Twój ogród w internecie"
      subtitle="Pokaż swój sezon innym ogrodnikom. Możesz włączyć profil pod własnym linkiem, kontrolować co jest widoczne i dzielić się postępami."
    >
      {saveError && (
        <div style={{
          padding: '12px 14px', borderRadius: 10,
          background: 'rgba(192, 106, 74, 0.08)',
          borderLeft: '3px solid var(--t-rust)',
          fontSize: 13, color: 'var(--t-text)', marginBottom: 22,
        }}>{saveError}</div>
      )}

      {/* Toggle enable + URL */}
      <div style={{
        padding: '20px 22px', borderRadius: 14,
        background: form.enabled ? 'rgba(168, 196, 158, 0.06)' : 'var(--t-bg-elev)',
        border: `1px solid ${form.enabled ? 'var(--t-sage)' : 'var(--t-line)'}`,
        marginBottom: 32,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginBottom: form.enabled && publicUrl ? 16 : 0,
        }}>
          <div>
            <Display size={18} style={{ display: 'block' }}>
              {form.enabled ? 'Profil publiczny aktywny' : 'Profil publiczny wyłączony'}
            </Display>
            <div style={{ fontSize: 12, color: 'var(--t-text-dim)', marginTop: 4 }}>
              {form.enabled
                ? 'Każdy ze ścieżką może zobaczyć Twój ogród'
                : 'Włącz, żeby otworzyć profil dla świata'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setField('enabled', !form.enabled)}
            aria-pressed={form.enabled}
            style={{
              width: 44, height: 26, borderRadius: 13,
              background: form.enabled ? 'var(--t-sage)' : 'var(--t-line-2)',
              position: 'relative', cursor: 'pointer',
              border: 'none', padding: 0,
              transition: 'background 200ms ease',
            }}
          >
            <div style={{
              position: 'absolute', top: 2,
              left: form.enabled ? 20 : 2,
              width: 22, height: 22, borderRadius: '50%',
              background: 'var(--t-bg)',
              transition: 'left 200ms ease',
            }} />
          </button>
        </div>

        {form.enabled && publicUrl && (
          <div style={{
            display: 'flex', gap: 8, alignItems: 'center',
            paddingTop: 14, borderTop: '1px solid var(--t-line)',
            flexWrap: 'wrap',
          }}>
            <code style={{
              flex: 1, minWidth: 200,
              padding: '8px 12px', borderRadius: 8,
              background: 'var(--t-bg)', border: '1px solid var(--t-line)',
              fontFamily: 'var(--t-mono)', fontSize: 12,
              color: 'var(--t-sage)', letterSpacing: '.04em',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{publicUrl}</code>
            <Button variant="ghost" onClick={handleCopyUrl}>Kopiuj</Button>
            <a
              href={`/g/${form.username}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontFamily: 'var(--t-mono)', fontSize: 11,
                letterSpacing: '.18em', textTransform: 'uppercase',
                color: 'var(--t-sage)', textDecoration: 'none',
                padding: '8px 12px',
              }}
            >Podgląd →</a>
          </div>
        )}
      </div>

      {/* Tożsamość */}
      <ProfileSection
        title="Tożsamość"
        description="Nazwa to fragment URL (gardenapp.pl/g/twoja-nazwa). Wyświetlana — to co widzą inni."
      >
        <div style={{ marginBottom: 16 }}>
          <ProfileLabel htmlFor="username">Nazwa użytkownika (slug URL)</ProfileLabel>
          <input
            id="username"
            type="text"
            value={form.username}
            onChange={e => setField('username', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ''))}
            placeholder="np. kasia-garden"
            pattern="[a-z0-9_-]{3,30}"
            minLength={3} maxLength={30}
            style={profileInputStyle}
          />
          <div style={{
            marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
          }}>3-30 znaków, tylko a-z, 0-9, myślnik, podkreślenie</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <ProfileLabel htmlFor="displayName">Nazwa wyświetlana</ProfileLabel>
            <input
              id="displayName" type="text"
              value={form.displayName}
              onChange={e => setField('displayName', e.target.value)}
              placeholder="np. Kasia"
              maxLength={50}
              style={profileInputStyle}
            />
          </div>
          <div>
            <ProfileLabel htmlFor="gardenType">Typ ogrodu</ProfileLabel>
            <select
              id="gardenType"
              value={form.gardenType}
              onChange={e => setField('gardenType', e.target.value)}
              style={{ ...profileInputStyle, colorScheme: 'dark' }}
            >
              {GARDEN_TYPES.map(g => (
                <option key={g.v} value={g.v}>{g.l}</option>
              ))}
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <ProfileLabel htmlFor="tagline">Tagline (jednowierszowy slogan)</ProfileLabel>
          <input
            id="tagline" type="text"
            value={form.tagline}
            onChange={e => setField('tagline', e.target.value)}
            placeholder="np. Warzywniak na 60 m² — od trzech sezonów"
            maxLength={100}
            style={profileInputStyle}
          />
          <div style={{
            marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{form.tagline.length} / 100</div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <ProfileLabel htmlFor="bio">Bio (krótkie)</ProfileLabel>
          <textarea
            id="bio"
            value={form.bio}
            onChange={e => setField('bio', e.target.value)}
            placeholder="Co chcesz powiedzieć o swoim ogrodzie?"
            maxLength={500}
            rows={3}
            style={{ ...profileInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
          />
          <div style={{
            marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{form.bio.length} / 500</div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <ProfileLabel htmlFor="aboutLong">Rozszerzony opis (opcjonalny)</ProfileLabel>
          <textarea
            id="aboutLong"
            value={form.aboutLong}
            onChange={e => setField('aboutLong', e.target.value)}
            placeholder="Dłuższa historia ogrodu — możesz opisać początki, ulubione rośliny, filozofię uprawy itd."
            maxLength={2000}
            rows={5}
            style={{ ...profileInputStyle, fontFamily: 'var(--t-sans)', resize: 'vertical' }}
          />
          <div style={{
            marginTop: 6, fontSize: 11, color: 'var(--t-text-mute)',
            fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            textAlign: 'right',
          }}>{form.aboutLong.length} / 2000</div>
        </div>
      </ProfileSection>

      {/* Lokalizacja */}
      <ProfileSection
        title="Lokalizacja w hero"
        description="Pokaż innym, gdzie i w jakiej strefie uprawiasz. Dane bierzemy z Profil → Mój ogród."
      >
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          marginBottom: 16,
          fontSize: 13, color: 'var(--t-text-dim)', lineHeight: 1.55,
        }}>
          {data?.city || data?.hardinessZone ? (
            <>
              <strong style={{ color: 'var(--t-text)' }}>Twój ogród:</strong>{' '}
              {data?.city || '— bez miasta —'}
              {data?.hardinessZone && ` · strefa ${data.hardinessZone}`}
              {data?.hasCoordinates && ' · pin dostępny'}
            </>
          ) : (
            <>
              ⚠ Brak lokalizacji w profilu. Dodaj miasto i strefę w
              <a href="/profile/garden" style={{ color: 'var(--t-sage)', marginLeft: 4 }}>Mój ogród</a>.
            </>
          )}
        </div>

        {[
          { k: 'showLocation', label: 'Pokazuj miasto i strefę', desc: 'Tagline w hero: „Strefa 7a · Warszawa-Wola"' },
          { k: 'showLocationPin', label: 'Pokazuj przybliżoną lokalizację (mapa)', desc: 'Pin zaokrąglony do ~10 km dla prywatności. Wymaga współrzędnych.' },
        ].map(opt => (
          <div key={opt.k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0',
            borderBottom: '1px solid var(--t-line)',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: 'var(--t-text)' }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3 }}>
                {opt.desc}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setField(opt.k, !form[opt.k])}
              aria-pressed={form[opt.k]}
              disabled={opt.k === 'showLocationPin' && !data?.hasCoordinates}
              style={{
                width: 36, height: 22, borderRadius: 11,
                background: form[opt.k] ? 'var(--t-sage)' : 'var(--t-line-2)',
                position: 'relative', cursor: 'pointer',
                border: 'none', padding: 0,
                flexShrink: 0,
                opacity: opt.k === 'showLocationPin' && !data?.hasCoordinates ? 0.4 : 1,
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: form[opt.k] ? 16 : 2,
                width: 18, height: 18, borderRadius: '50%',
                background: 'var(--t-bg)',
                transition: 'left 200ms ease',
              }} />
            </button>
          </div>
        ))}
      </ProfileSection>

      {/* Roślina sezonu (featured) */}
      <ProfileSection
        title="Roślina sezonu"
        description="Wyróżnij jedną grządkę — pojawi się jako duży kafelek w hero („właśnie kwitnie”, „ostatnio zebrane”)."
      >
        {beds.length === 0 ? (
          <div style={{
            padding: '14px 16px', borderRadius: 10,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
            fontSize: 13, color: 'var(--t-text-dim)',
          }}>
            ↳ Nie masz jeszcze grządek do wyróżnienia. Dodaj je w <a href="/plots" style={{ color: 'var(--t-sage)' }}>Działki</a>.
          </div>
        ) : (
          <>
            <ProfileLabel htmlFor="featuredBed">Wybierz grządkę do wyróżnienia</ProfileLabel>
            <select
              id="featuredBed"
              value={form.featuredBedId || ''}
              onChange={e => setField('featuredBedId', e.target.value)}
              style={{ ...profileInputStyle, colorScheme: 'dark' }}
            >
              <option value="">— bez wyróżnienia —</option>
              {beds.map(b => (
                <option key={b.id} value={b.id}>{b.label}</option>
              ))}
            </select>
            {featuredBedLabel && (
              <div style={{
                marginTop: 10, padding: '10px 14px', borderRadius: 8,
                background: 'rgba(168, 196, 158, 0.08)',
                fontSize: 12, color: 'var(--t-text-dim)',
                fontStyle: 'italic',
              }}>
                ✓ Wybrane: {featuredBedLabel}
              </div>
            )}
          </>
        )}
      </ProfileSection>

      {/* Cover photo */}
      <ProfileSection
        title="Zdjęcie tła profilu (cover)"
        description="Główne zdjęcie pojawiające się na górze profilu. Najlepiej szerokie ujęcie ogrodu albo ulubiona kompozycja."
      >
        {coverPhoto ? (
          <div style={{
            display: 'flex', gap: 14, alignItems: 'center',
            padding: '14px 16px', borderRadius: 12,
            background: 'var(--t-bg-elev)',
            border: '1px solid var(--t-line)',
          }}>
            <img
              src={coverPhoto.path?.startsWith('/') ? coverPhoto.path : `/${coverPhoto.path}`}
              alt=""
              style={{
                width: 80, height: 80, borderRadius: 8,
                objectFit: 'cover', flexShrink: 0,
              }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, color: 'var(--t-text)', fontWeight: 500 }}>
                {coverPhoto.caption || coverPhoto.plantName || 'Bez podpisu'}
              </div>
              {coverPhoto.plotName && (
                <div style={{ fontSize: 11, color: 'var(--t-text-mute)', marginTop: 2 }}>
                  {coverPhoto.plotName}
                </div>
              )}
            </div>
            <Button variant="ghost" onClick={() => setPickerMode('cover')}>Zmień</Button>
            <button
              type="button"
              onClick={() => setField('coverPhotoId', null)}
              style={{
                background: 'transparent', border: 'none',
                color: 'var(--t-rust)', cursor: 'pointer',
                fontSize: 18, padding: 4,
              }}
              title="Usuń"
            >×</button>
          </div>
        ) : (
          <Button variant="ghost" onClick={() => setPickerMode('cover')}>
            + Wybierz zdjęcie tła
          </Button>
        )}
      </ProfileSection>

      {/* Galeria publiczna */}
      <ProfileSection
        title="Galeria publiczna"
        description="Wybierz do 50 zdjęć z Twojej galerii — pojawią się w sekcji „Galeria” na profilu."
      >
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: 'var(--t-bg-elev)',
          border: '1px solid var(--t-line)',
          marginBottom: 14,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          flexWrap: 'wrap', gap: 12,
        }}>
          <div>
            <div style={{ fontSize: 14, color: 'var(--t-text)' }}>
              Wybranych zdjęć: <strong style={{ color: 'var(--t-sage)' }}>{galleryIds.length}</strong>
              {galleryDirty && <Chip tone="amber" style={{ marginLeft: 8 }}>niezapisane</Chip>}
            </div>
            <div style={{
              fontSize: 11, color: 'var(--t-text-mute)', marginTop: 4,
              fontFamily: 'var(--t-mono)', letterSpacing: '.06em',
            }}>
              max. 50 · z {availablePhotos?.length || 0} dostępnych
            </div>
          </div>
          <Button variant="ghost" onClick={() => setPickerMode('gallery')}>
            {galleryIds.length > 0 ? 'Edytuj wybór' : '+ Wybierz zdjęcia'}
          </Button>
        </div>
      </ProfileSection>

      {/* Co pokazywać */}
      <ProfileSection
        title="Co pokazywać"
        description="Sekcje, które chcesz, żeby były widoczne na profilu publicznym."
      >
        {[
          { k: 'showStats',    label: 'Statystyki sezonu', desc: 'Liczba działek, grządek, plony w kg' },
          { k: 'showTimeline', label: 'Co teraz rośnie', desc: 'Lista grządek z paskiem postępu' },
          { k: 'showGallery',  label: 'Galeria zdjęć', desc: 'Wybrane zdjęcia z Twoich grządek' },
          { k: 'showBadges',   label: 'Odznaki', desc: 'Osiągnięcia (np. pierwszy zbiór, mistrz pomidora)' },
        ].map(opt => (
          <div key={opt.k} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0',
            borderBottom: '1px solid var(--t-line)',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: 'var(--t-text)' }}>{opt.label}</div>
              <div style={{ fontSize: 11, color: 'var(--t-text-dim)', marginTop: 3 }}>
                {opt.desc}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setField(opt.k, !form[opt.k])}
              aria-pressed={form[opt.k]}
              style={{
                width: 36, height: 22, borderRadius: 11,
                background: form[opt.k] ? 'var(--t-sage)' : 'var(--t-line-2)',
                position: 'relative', cursor: 'pointer',
                border: 'none', padding: 0,
                flexShrink: 0,
              }}
            >
              <div style={{
                position: 'absolute', top: 2,
                left: form[opt.k] ? 16 : 2,
                width: 18, height: 18, borderRadius: '50%',
                background: 'var(--t-bg)',
                transition: 'left 200ms ease',
              }} />
            </button>
          </div>
        ))}
      </ProfileSection>

      {/* Linki społecznościowe */}
      <ProfileSection
        title="Linki społecznościowe"
        description="Opcjonalne — pojawią się jako ikony w hero profilu."
      >
        {['socialInstagram', 'socialFacebook', 'socialYoutube', 'socialPinterest', 'socialWebsite'].map(key => (
          <div key={key} style={{ marginBottom: 14 }}>
            <ProfileLabel htmlFor={key}>{SOCIAL_LABELS[key]}</ProfileLabel>
            <input
              id={key} type="url"
              value={form[key]}
              onChange={e => setField(key, e.target.value)}
              placeholder={SOCIAL_PLACEHOLDERS[key]}
              maxLength={200}
              style={profileInputStyle}
            />
          </div>
        ))}
      </ProfileSection>

      {/* Stats preview */}
      {stats && form.enabled && (
        <ProfileSection
          title="Statystyki widoków"
          description="Ile osób odwiedziło Twój profil publiczny."
        >
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            {[
              { label: 'Łącznie',       value: stats.totalViews || 0 },
              { label: 'Ostatnie 7 dni', value: stats.viewsLast7Days || 0 },
              { label: 'Ostatnie 30 dni',value: stats.viewsLast30Days || 0 },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1, minWidth: 120,
                padding: '14px 16px', borderRadius: 12,
                background: 'var(--t-bg-elev)',
                border: '1px solid var(--t-line)',
              }}>
                <Meta style={{ marginBottom: 6 }}>{s.label}</Meta>
                <Display size={24} style={{ display: 'block' }}>{s.value}</Display>
              </div>
            ))}
          </div>
        </ProfileSection>
      )}

      {/* Sticky save bar */}
      <div style={{
        position: 'sticky', bottom: 16,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 14, padding: '14px 16px', marginTop: 24,
        borderRadius: 12,
        background: isDirty ? 'rgba(220, 180, 100, 0.08)' : 'transparent',
        border: isDirty ? '1px solid var(--t-amber)' : '1px solid transparent',
      }}>
        {savedAt && !isDirty ? (
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
            color: 'var(--t-sage)',
          }}>✓ Zapisano</div>
        ) : isDirty ? (
          <div style={{
            fontFamily: 'var(--t-mono)', fontSize: 11,
            letterSpacing: '.14em', textTransform: 'uppercase',
            color: 'var(--t-amber)',
          }}>Niezapisane zmiany</div>
        ) : <div />}
        <Button
          onClick={handleSave}
          variant={isDirty && !saving ? 'primary' : 'ghost'}
          disabled={!isDirty || saving}
        >{saving ? 'Zapisuję…' : 'Zapisz zmiany'}</Button>
      </div>

      <PhotoPickerModal
        open={pickerMode !== null}
        mode={pickerMode}
        currentCoverId={form.coverPhotoId}
        currentGalleryIds={galleryIds}
        onClose={() => setPickerMode(null)}
        onSave={(value) => {
          if (pickerMode === 'cover') setField('coverPhotoId', value);
          else setGalleryIds(value);
          setPickerMode(null);
        }}
      />
    </ProfileShell>
  );
}
